import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  FileText, Upload, Download, Trash2, Search, RefreshCw,
  File, Image, FileSpreadsheet, Building2, Users, BookOpen,
  HardHat, Shield, Truck
} from 'lucide-react';
import { documentsAPI } from '../services/api';

// Company document categories with icons
const companyDocumentTabs = [
  { id: 'administration', label: 'Administration', icon: Building2 },
  { id: 'clients', label: 'Clients', icon: Users },
  { id: 'codes_standards', label: 'Codes & Standards', icon: BookOpen },
  { id: 'contractors', label: 'Contractors', icon: HardHat },
  { id: 'ohs', label: 'OH&S', icon: Shield },
  { id: 'suppliers', label: 'Suppliers', icon: Truck },
];

const getFileIcon = (mimeType, filename) => {
  if (mimeType?.startsWith('image/')) return <Image className="h-4 w-4 text-blue-400" />;
  if (mimeType === 'application/pdf') return <FileText className="h-4 w-4 text-red-400" />;
  if (mimeType?.includes('spreadsheet') || filename?.endsWith('.csv')) return <FileSpreadsheet className="h-4 w-4 text-green-400" />;
  return <File className="h-4 w-4 text-gray-400" />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const DocumentsPage = () => {
  const [activeTab, setActiveTab] = useState('administration');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        scope: 'company',
        document_type: activeTab
      };
      const response = await documentsAPI.getAll(params);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', activeTab);
      formData.append('scope', 'company');
      if (uploadDescription) formData.append('description', uploadDescription);

      await documentsAPI.upload(formData);
      toast.success('Document uploaded successfully');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDescription('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (doc) => {
    const link = document.createElement('a');
    const t = localStorage.getItem('token');
    link.href = `${process.env.REACT_APP_BACKEND_URL}${doc.file_path}${t ? `?token=${encodeURIComponent(t)}` : ''}`;
    link.download = doc.original_filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      await documentsAPI.delete(doc.id);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Filter documents by search query
  const filteredDocuments = documents.filter((doc) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      doc.original_filename?.toLowerCase().includes(query) ||
      doc.description?.toLowerCase().includes(query)
    );
  });

  const currentTabInfo = companyDocumentTabs.find(t => t.id === activeTab);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Company Documents</h1>
          <p className="text-muted-foreground">Manage company-wide files and documentation</p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50 flex-wrap h-auto p-1">
          {companyDocumentTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {companyDocumentTabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="space-y-4">
            {/* Tab Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={`Search ${tab.label.toLowerCase()}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-background"
                  />
                </div>
                <Button variant="ghost" size="icon" onClick={fetchDocuments}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <Button onClick={() => setUploadOpen(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload to {tab.label}
              </Button>
            </div>

            {/* Documents List */}
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  {React.createElement(tab.icon, { className: "h-5 w-5 text-primary" })}
                  {tab.label} Documents
                  <span className="text-muted-foreground font-normal">({filteredDocuments.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mb-4 opacity-50" />
                    <p>No documents in {tab.label}</p>
                    <Button variant="link" onClick={() => setUploadOpen(true)}>
                      Upload a document
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredDocuments.map((doc) => (
                      <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-secondary/20 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          {getFileIcon(doc.mime_type, doc.original_filename)}
                          <div className="min-w-0">
                            <p className="font-medium truncate">{doc.original_filename}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span>•</span>
                              <span>{formatDate(doc.created_at)}</span>
                              {doc.uploaded_by_name && (
                                <>
                                  <span>•</span>
                                  <span>by {doc.uploaded_by_name}</span>
                                </>
                              )}
                            </div>
                            {doc.description && (
                              <p className="text-xs text-muted-foreground mt-1 truncate">{doc.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-4">
                          <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)} className="h-8 w-8">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(doc)} className="h-8 w-8 text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload to {currentTabInfo?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">File</label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="bg-background"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              />
              {uploadFile && (
                <p className="text-xs text-muted-foreground mt-1">
                  {uploadFile.name} ({formatFileSize(uploadFile.size)})
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description (Optional)</label>
              <Input
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Brief description..."
                className="bg-background"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsPage;
